import React from "react";
import PropTypes from "prop-types";
import { prProfileUrl, companyUrl } from "views_shared/app/javascript/utils/urls";
import MessageButton from "../../MessageButton";
import LocationMarkerIcon from "../../profiles/LocationMarkerIcon";
import SavedGroupsBookmark from "../../source_directory/SavedGroupsBookmark";
import SourceAvatar from "../../source_directory/SourceAvatar";
import { SourceBadgeList } from "../../source_directory/SourceBadges";
import HashtagPills from "../../source_directory/HashtagPills";
import HighlightAttribute from "../../source_directory/HighlightAttribute";
import prListActionColumnWidth from "./listActionColumn";
import isOwnPrProfile from "./viewerGating";
import { prCardRepresentsHref } from "../show/pr_sections";

function PrCardList({ hit }) {
  const isSignedIn = !!globalThis.Qwoted?.CURRENT_USER_ID;
  const canMessage = isSignedIn && !isOwnPrProfile(hit.id);
  const hashtags = hit.hashtags || [];
  const represents = [
    hit.experts_count > 0 && { count: hit.experts_count, label: hit.experts_count === 1 ? "Expert" : "Experts" },
    hit.products_count > 0 && { count: hit.products_count, label: hit.products_count === 1 ? "Product" : "Products" },
  ].filter(Boolean);

  return (
    <div
      className="sd-pr-list-row"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "12px 16px",
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
      }}
    >
      {/* Name / photo */}
      <div
        className="sd-pr-list-row__col"
        style={{ display: "flex", alignItems: "flex-start", gap: 10, flex: "0 0 200px", minWidth: 0 }}
      >
        <SourceAvatar imageUrl={hit.profile_image_url} name={hit.name} initialsText={hit.initials} size={44} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>
            <a href={prProfileUrl({ slug: hit.slug, migrate_id: hit.migrate_id })} style={{ color: "#111827" }}>
              <HighlightAttribute hit={hit} attribute="name" fallback={hit.name} />
            </a>
          </div>
          {hit.current_position_workplace && (
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              PR at{" "}
              {hit.current_position_workplace_slug ? (
                <a
                  href={companyUrl({
                    slug: hit.current_position_workplace_slug,
                    migrate_id: hit.current_position_workplace_migrate_id,
                  })}
                  style={{ color: "#4d5357", textDecoration: "underline" }}
                >
                  <HighlightAttribute
                    hit={hit}
                    attribute="current_position_workplace"
                    fallback={hit.current_position_workplace}
                  />
                </a>
              ) : (
                <HighlightAttribute
                  hit={hit}
                  attribute="current_position_workplace"
                  fallback={hit.current_position_workplace}
                />
              )}
            </div>
          )}
          <SourceBadgeList
            size="sm"
            marginTop={2}
            types={[hit.qwoted100 && "qwoted100", hit.quick_to_respond && "fast_to_respond"]}
          />
          {hit.location_string && (
            <div
              style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#6b7280", marginTop: 2 }}
            >
              <LocationMarkerIcon size={12} />
              {hit.location_string}
            </div>
          )}
        </div>
      </div>

      {/* Covers */}
      <div className="sd-pr-list-row__col" style={{ flex: 1, minWidth: 0 }}>
        <HashtagPills hashtags={hashtags} max={3} />
      </div>

      {/* Represents */}
      <div
        className="sd-pr-list-row__col"
        style={{ flex: "0 0 140px", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}
      >
        {represents.map((r) => (
          <a
            key={r.label}
            href={prCardRepresentsHref(hit)}
            style={{ fontSize: 14, color: "#212528", textDecoration: "underline" }}
          >
            <strong>{r.count}</strong> {r.label}
          </a>
        ))}
      </div>

      {/* Actions — fixed-width column kept in sync with the list header */}
      <div
        className="sd-pr-list-row__col"
        style={{
          flex: `0 0 ${prListActionColumnWidth(isSignedIn)}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 12,
        }}
      >
        {canMessage && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <MessageButton
              profileId={hit.id}
              buttonLabel="Start a Conversation"
              extraButtonClasses="mb-0 w-100"
              openInboxThread
            />
          </div>
        )}
        <SavedGroupsBookmark
          sourceableType="Id::Profile::Promoter"
          sourceableId={hit.id}
          sourceMeta={{ name: hit.name, imageUrl: hit.profile_image_url, slug: hit.slug }}
        />
      </div>
    </div>
  );
}

PrCardList.propTypes = {
  hit: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    slug: PropTypes.string.isRequired,
    name: PropTypes.string,
    profile_image_url: PropTypes.string,
    initials: PropTypes.string,
    current_position_workplace: PropTypes.string,
    current_position_workplace_slug: PropTypes.string,
    location_string: PropTypes.string,
    hashtags: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          hashtag_slug: PropTypes.string,
          hashtag_name: PropTypes.string,
          hashtagName: PropTypes.string,
        }),
      ]),
    ),
    experts_count: PropTypes.number,
    products_count: PropTypes.number,
    qwoted100: PropTypes.bool,
    quick_to_respond: PropTypes.bool,
  }).isRequired,
};

export default PrCardList;
